// The properties panel's rows: a title, an info line, a note, a button, a
// number / text / select / checkbox / colour / slider field, each label English
// and translated here; and parseMeasure, the one reader of a typed length.
// docs/claude/ui.md.
import { defaultName } from '../../shared/names';
import { shortcutLabel } from '../../shared/shortcuts';
import { type TextI18n } from '../../shared/types';
import {
  beginGesture,
  commitGesture,
  invalidate,
  pushHistory,
  renderProperties,
} from './core';
import {
  getUiLang,
  tr,
} from '../i18n';
import { iconMarkup } from '../icons';
import { nameDefaultsRows } from './nameFields';
import { savedColors } from './standards';
import {
  normalizeColor,
  noteChangeInStep,
} from '../main';
import { deleteSelected } from './deleteSelection';

/** A colour-picker + hex/rgb text row. */
/** Show `hex` in a `colorRow` without writing anything, or "(mixed)" for null. */
export function showColorRow(row: HTMLElement, hex: string | null): void {
  const picker = row.querySelector<HTMLInputElement>('input[type="color"]');
  const text = row.querySelector<HTMLInputElement>('input[type="text"]');
  const n = hex == null ? null : normalizeColor(hex);
  if (picker && n) picker.value = n;
  if (text) { text.value = n ?? ''; text.placeholder = n ? '' : tr('(mixed)'); }
}

/** A colour field (picker, hex text, saved swatches) that records ONE undo step
 *  per colour change itself — `onChange` only changes the colour, never pushes.
 *  A picker dragged records it on its first input and changes the rest live,
 *  until it closes. `ownStep`: the caller records it, when `first` says a change
 *  begins (the title block's, a foreign entry). */
export function colorRow(label: string, value: string, onChange: (hex: string, first: boolean) => void, opts: { onClear?: () => string; ownStep?: boolean } = {}): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const lab = document.createElement('label');
  lab.textContent = tr(label);
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.flexDirection = 'column';
  wrap.style.gap = '6px';
  wrap.style.flex = '1';
  const top = document.createElement('div');
  top.style.display = 'flex';
  top.style.gap = '6px';
  const picker = document.createElement('input');
  picker.type = 'color';
  picker.value = value;
  picker.style.width = '28px';
  picker.style.padding = '0';
  const hex = document.createElement('input');
  hex.type = 'text';
  hex.value = value;
  let dragging = false;
  const apply = (raw: string, drag = false) => {
    const n = normalizeColor(raw);
    if (n) {
      const first = !(drag && dragging);
      if (!opts.ownStep) { if (first) pushHistory(); else noteChangeInStep(); }
      dragging = drag;
      picker.value = n;
      hex.value = n;
      onChange(n, first);
      invalidate();
    } else {
      hex.value = picker.value;
    }
  };
  picker.addEventListener('input', () => apply(picker.value, true));
  picker.addEventListener('change', () => { dragging = false; });
  hex.addEventListener('change', () => {
    if (opts.onClear && !hex.value.trim()) {
      beginGesture();
      const shown = opts.onClear();
      commitGesture();
      showColorRow(row, shown);
      invalidate();
      return;
    }
    apply(hex.value);
  });
  top.append(picker, hex);
  wrap.append(top, savedColorSwatches(apply));
  row.append(lab, wrap);
  return row;
}

/**
 * A strip of quick-pick swatches for the shared named-colour palette. Each
 * swatch shows the colour and a tooltip with its name; clicking applies it.
 * Returns an empty container when no saved colours are available.
 */
export function savedColorSwatches(apply: (hex: string) => void): HTMLElement {
  const strip = document.createElement('div');
  strip.className = 'color-swatches';
  strip.style.display = 'flex';
  strip.style.flexWrap = 'wrap';
  strip.style.gap = '4px';
  for (const sc of savedColors) {
    const n = normalizeColor(sc.color);
    if (!n) continue;
    const sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'color-swatch';
    sw.title = sc.name ? `${sc.name} (${n})` : n;
    sw.style.cssText =
      `width:16px;height:16px;padding:0;border-radius:3px;cursor:pointer;` +
      `background:${n};border:1px solid rgba(0,0,0,0.35);`;
    sw.addEventListener('click', () => apply(sc.color));
    strip.appendChild(sw);
  }
  return strip;
}

/** A labelled range slider over an arbitrary numeric range (min…max, mm and the
 *  like), with a live read-out and a single undo step per drag — the general
 *  twin of `opacityRow`'s 0–100 % slider. */
export function sliderRow(label: string, value: number, min: number, max: number, step: number, onChange: (v: number) => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const lab = document.createElement('label');
  lab.textContent = tr(label);
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.gap = '8px';
  wrap.style.flex = '1';
  wrap.style.alignItems = 'center';
  const range = document.createElement('input');
  range.type = 'range';
  range.className = 'opacity-range';
  range.min = String(min);
  range.max = String(max);
  range.step = String(step);
  const clamped = Math.max(min, Math.min(max, value));
  range.value = String(clamped);
  range.style.flex = '1';
  const paint = (): void => range.style.setProperty('--fill', `${((parseFloat(range.value) - min) / Math.max(1e-6, max - min)) * 100}%`);
  paint();
  const out = document.createElement('span');
  out.className = 'prop-value';
  out.style.minWidth = '34px';
  out.textContent = String(Math.round(clamped));
  // Snapshot once, before the first change, so undo restores the pre-drag value.
  let pushed = false;
  range.addEventListener('input', () => {
    if (!pushed) { pushHistory(); pushed = true; }
    const v = parseFloat(range.value);
    out.textContent = String(Math.round(v));
    paint();
    onChange(v);
    invalidate();
  });
  range.addEventListener('change', () => { pushed = false; });
  wrap.append(range, out);
  row.append(lab, wrap);
  return row;
}

/** An opacity slider row (0..1). */
export function opacityRow(label: string, value: number, onChange: (v: number) => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const lab = document.createElement('label');
  lab.textContent = tr(label);
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.gap = '8px';
  wrap.style.flex = '1';
  wrap.style.alignItems = 'center';
  const range = document.createElement('input');
  range.type = 'range';
  range.className = 'opacity-range';
  range.min = '0';
  range.max = '100';
  range.value = String(Math.round(value * 100));
  range.style.flex = '1';
  // Paint the track fill up to the current value so 100% looks 100% full
  // (native thumbs are inset, leaving a plain slider ~10% short at the end).
  const paint = (): void => range.style.setProperty('--fill', `${range.value}%`);
  paint();
  const out = document.createElement('span');
  out.className = 'prop-value';
  out.style.minWidth = '34px';
  out.textContent = `${Math.round(value * 100)}%`;
  // Snapshot once, before the drag's first change, so undo restores the value
  // the slider started at (pushing on `change` would snapshot the new value).
  let pushed = false;
  range.addEventListener('input', () => {
    if (!pushed) { pushHistory(); pushed = true; }
    const v = parseInt(range.value) / 100;
    out.textContent = `${range.value}%`;
    paint();
    onChange(v);
    invalidate();
  });
  range.addEventListener('change', () => { pushed = false; });
  wrap.append(range, out);
  row.append(lab, wrap);
  return row;
}

/** The ONE Delete button: every properties panel for a selection ends with it
 *  (`renderProperties` adds it where a panel did not), in one style, with the key
 *  that does the same. `groupPropCards` keeps it out of the cards, last. An
 *  `inPlace` one deletes something other than the selection (a sheet) and stays
 *  where it was put, in the same style. */
export function deleteRow(onClick: () => void = deleteSelected, label = 'Delete', inPlace = false): HTMLElement {
  const row = document.createElement('div');
  row.className = inPlace ? 'prop-delete-row' : 'prop-delete prop-delete-row';
  const btn = document.createElement('button');
  btn.className = 'danger';
  btn.innerHTML = iconMarkup('btn-delete');
  const text = document.createElement('span'); text.textContent = tr(label);
  const key = document.createElement('span'); key.className = 'prop-delete-key'; key.textContent = shortcutLabel('delete', tr);
  btn.append(text, key);
  btn.addEventListener('click', onClick);
  row.appendChild(btn);
  return row;
}

export function buttonRow(text: string, onClick: () => void): HTMLElement {
  const row = document.createElement('div');
  row.style.margin = '6px 0';
  const btn = document.createElement('button');
  btn.textContent = tr(text);
  btn.style.width = '100%';
  btn.addEventListener('click', onClick);
  row.appendChild(btn);
  return row;
}

/** A note under a panel's rows (a warning, or what the ceiling does to the
 *  object): English, translated here — or `ready`, already translated. */
export function noteRow(text: string, kind: 'warn' = 'warn', ready = false): HTMLElement {
  const d = document.createElement('div');
  d.className = `prop-note ${kind}`;
  d.textContent = ready ? text : tr(text);
  return d;
}

export function infoRow(label: string, value: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const l = document.createElement('label');
  l.textContent = tr(label);
  const val = document.createElement('div');
  val.className = 'prop-value';
  val.textContent = value;
  row.append(l, val);
  return row;
}

/** An editable "Name" text row. `current` is shown as a placeholder when the
 *  object has no explicit name yet (the auto-numbered default), so the field
 *  looks empty until the user types a real name. */
/** THE row for a name the user can enter (docs/claude/names.md §1): the
 *  entered name, editable, its placeholder the default in the UI language;
 *  under it the default in each language, read-only. Typing renames live and a
 *  change is the undo step; cleared, the default comes back. `set` gets the
 *  text as typed. */
export function nameRow(entered: string | undefined, defaults: TextI18n | undefined, set: (v: string) => void, opts: { base?: string; commit?: boolean } = {}): HTMLElement {
  const base = opts.base ?? '';
  const wrap = document.createElement('div');
  wrap.className = 'name-row';
  const row = document.createElement('div');
  row.className = 'prop-row';
  const l = document.createElement('label');
  l.textContent = tr('Name');
  const input = document.createElement('input');
  input.type = 'text';
  input.value = entered ?? '';
  input.placeholder = defaultName(defaults, getUiLang(), base);
  // `commit`: the setter makes its own undo step, once the name is typed.
  if (opts.commit) input.addEventListener('change', () => set(input.value));
  else {
    input.addEventListener('input', () => set(input.value));
    input.addEventListener('change', () => pushHistory());
  }
  row.append(l, input);
  wrap.append(row, nameDefaultsRows(defaults, base));
  return wrap;
}

export function sectionTitle(text: string): HTMLElement {
  const d = document.createElement('div');
  d.className = 'prop-section-title';
  d.textContent = tr(text);
  return d;
}

/**
 * A labelled number input. By default it reports every keystroke (`input`),
 * which is fine for fields that only redraw the canvas. Pass `{ commit: true }`
 * for fields whose handler rebuilds the properties panel (width, depth, …):
 * those commit on `change` (blur / Enter) instead, so the input the user is
 * typing into is not destroyed mid-edit.
 */
/**
 * Evaluate a numeric formula typed into a dimension input.
 * Accepts plain numbers ("1500") and basic arithmetic ("1500+100", "2000/3",
 * "(400+600)*2"). Only digits, spaces, and +-*\/().  are allowed — no JS
 * identifiers — so there is no code-injection risk even in Electron.
 */
export function evalFormula(s: string): number | null {
  const t = s.trim().replace(/,/g, '.'); // accept a comma as the decimal separator
  if (!t) return null;
  if (!/^[\d\s+\-*/.()]+$/.test(t)) return null;
  // Recursive-descent evaluator (no eval / Function): the app's CSP forbids
  // 'unsafe-eval', so a JS-runtime evaluator would throw at runtime.
  let i = 0;
  const skip = () => { while (i < t.length && t[i] === ' ') i++; };
  // expr := term (('+' | '-') term)*
  const parseExpr = (): number => {
    let v = parseTerm();
    for (;;) {
      skip();
      const c = t[i];
      if (c === '+') { i++; v += parseTerm(); }
      else if (c === '-') { i++; v -= parseTerm(); }
      else break;
    }
    return v;
  };
  // term := factor (('*' | '/') factor)*
  const parseTerm = (): number => {
    let v = parseFactor();
    for (;;) {
      skip();
      const c = t[i];
      if (c === '*') { i++; v *= parseFactor(); }
      else if (c === '/') { i++; v /= parseFactor(); }
      else break;
    }
    return v;
  };
  // factor := ('+' | '-')* ( number | '(' expr ')' )
  const parseFactor = (): number => {
    skip();
    if (t[i] === '+') { i++; return parseFactor(); }
    if (t[i] === '-') { i++; return -parseFactor(); }
    if (t[i] === '(') {
      i++;
      const v = parseExpr();
      skip();
      if (t[i] !== ')') throw new Error('unbalanced');
      i++;
      return v;
    }
    const start = i;
    while (i < t.length && /[\d.]/.test(t[i])) i++;
    if (i === start) throw new Error('expected number');
    const n = parseFloat(t.slice(start, i));
    if (!isFinite(n)) throw new Error('bad number');
    return n;
  };
  try {
    const v = parseExpr();
    skip();
    if (i !== t.length) return null; // trailing junk ⇒ malformed
    return isFinite(v) ? v : null;
  } catch { /* malformed expression */ }
  return null;
}

export type LenUnit = 'mm' | 'cm' | 'm';
const UNIT_MM: Record<LenUnit, number> = { mm: 1, cm: 10, m: 1000 };
// Every input unit the app accepts, in millimetres. The metric ones convert
// exactly; ft/in give the closest millimetre representation (1 in = 25.4 mm,
// 1 ft = 304.8 mm), rounded to whole millimetres by the field's own rounding.
const INPUT_UNIT_MM: Record<string, number> = { mm: 1, cm: 10, m: 1000, ft: 304.8, in: 25.4 };
// Matches the accepted unit suffixes. Longer / more-specific tokens come first so
// "mm" wins over "m" and the two-letter imperial tokens match whole.
const UNIT_RE = /mm|cm|ft|in|m/g;

/**
 * Parse a measurement into a number expressed in `fieldUnit`. Every linear input
 * defaults to millimetres: a bare number is taken to be in the field's own unit
 * (mm unless the label says cm / m). A trailing unit suffix (mm / cm / m / ft /
 * in) overrides that and is converted to the field unit. Accepts comma or dot
 * decimals and arithmetic (+ - * / and parentheses):
 *   "0,4 m" / "0.4 m" / "40 cm" / "400 mm"  → 400   (in a mm field)
 *   "1 in"                                  → 25.4  (in a mm field)
 *   "2 ft"                                  → 609.6 (in a mm field)
 *   "50+20 cm"                              → 700   (in a mm field)
 *   "(20*3)+10"                             → 70    (no unit ⇒ the field's own unit)
 * Returns null for an invalid expression.
 */
export function parseMeasure(raw: string, fieldUnit: LenUnit = 'mm'): number | null {
  const lower = raw.trim().toLowerCase();
  if (!lower) return null;
  // The last unit token sets the input unit (a unit trails the value it measures).
  UNIT_RE.lastIndex = 0;
  let m: RegExpExecArray | null, unit: string | null = null;
  while ((m = UNIT_RE.exec(lower)) !== null) unit = m[0];
  const expr = lower.replace(UNIT_RE, ' '); // strip units, keep the arithmetic
  const v = evalFormula(expr);
  if (v === null) return null;
  return unit ? (v * INPUT_UNIT_MM[unit]) / UNIT_MM[fieldUnit] : v;
}

/** The length unit named in a field label like "Width (mm)" / "Depth (cm)", or
 *  null for a non-length field (degrees, counts, …). */
function labelUnit(label: string): LenUnit | null {
  const m = /\((mm|cm|m)\)/i.exec(label);
  return m ? (m[1].toLowerCase() as LenUnit) : null;
}


/** A labelled free-text row (returns the row; the input is passed to `onCommit`). */
export function textFieldRow(label: string, value: string, placeholder: string, onCommit: (v: string) => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const l = document.createElement('label');
  l.textContent = tr(label);
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value;
  input.placeholder = tr(placeholder);
  input.addEventListener('change', () => onCommit(input.value));
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') input.blur(); });
  row.append(l, input);
  return row;
}

/** A labelled dropdown row; option texts are English and translated here. */
export function selectRow(label: string, options: ReadonlyArray<readonly [string, string]>, value: string, onChange: (v: string) => void,
  opts: { disabled?: ReadonlyMap<string, string> } = {}): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const l = document.createElement('label');
  l.textContent = tr(label);
  const sel = document.createElement('select');
  for (const [v, text] of options) {
    const opt = document.createElement('option');
    opt.value = v;
    opt.textContent = tr(text);
    const why = opts.disabled?.get(v);
    if (why) { opt.disabled = true; opt.title = tr(why); } // a greyed option says why
    sel.appendChild(opt);
  }
  sel.value = value;
  sel.addEventListener('change', () => onChange(sel.value));
  row.append(l, sel);
  return row;
}

/** A number field. `value` null shows it empty; with `onClear`, committing an
 *  empty field calls it (an optional figure that falls back to a default, which
 *  `placeholder` shows). */
/** The one Height from ground row every object's panel shows — its underside in
 *  mm above the ground, typed whole, one undo step; `min` 0 for a feature of a
 *  wall or a room, which never goes under its floor. `value` null: mixed. */
export function heightFromGroundRow(value: number | null, set: (v: number) => void, min = -Infinity): HTMLElement {
  return numberRow('Height from ground (mm)', value == null ? null : Math.round(value), (v) => {
    pushHistory(); set(Math.max(min, Math.round(v))); renderProperties(); invalidate();
  }, { commit: true });
}

export function numberRow(
  label: string,
  value: number | null,
  onChange: (v: number) => void,
  opts: { commit?: boolean; disabled?: boolean; placeholder?: string; onClear?: () => void } = {}
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row' + (opts.disabled ? ' disabled' : '');
  const l = document.createElement('label');
  l.textContent = tr(label); // display translated; unit detection below stays on the English label
  const input = document.createElement('input');
  input.type = 'text';
  input.inputMode = 'decimal';
  input.value = value == null ? '' : String(Math.round(value * 100) / 100);
  if (opts.placeholder != null) input.placeholder = opts.placeholder;
  if (opts.disabled) input.disabled = true;
  // A length field (label ends in "(mm)"/"(cm)"/"(m)") accepts any unit and
  // converts to its own unit; other fields (°, counts) take a plain number.
  const unit = labelUnit(label);
  const parse = (s: string): number | null => (unit ? parseMeasure(s, unit) : evalFormula(s));
  const fire = (commit: boolean) => {
    if (opts.onClear && !input.value.trim()) { if (commit) opts.onClear(); return; }
    const v = parse(input.value);
    if (v !== null) {
      onChange(v);
      if (commit) input.value = String(Math.round(v * 100) / 100);
    }
  };
  if (opts.commit) {
    input.addEventListener('change', () => fire(true));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') input.blur();
    });
  } else {
    input.addEventListener('input', () => fire(false));
  }
  row.append(l, input);
  return row;
}

/** A labelled checkbox row; the label is English and translated here. */
export function checkboxRow(label: string, checked: boolean, onChange: (v: boolean) => void): HTMLElement {
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr(label);
  const box = document.createElement('input'); box.type = 'checkbox'; box.checked = checked;
  box.addEventListener('change', () => onChange(box.checked));
  row.append(lab, box);
  return row;
}

/** Wrap a control with a small caption above it (clicking the caption focuses it). */
export function labeledField(label: string, control: HTMLElement, span = false): HTMLElement {
  const f = document.createElement('label');
  f.className = span ? 'field span-2' : 'field';
  const l = document.createElement('span');
  l.className = 'field-label';
  l.textContent = tr(label);
  f.append(l, control);
  return f;
}
